/**
 * PortalActionChrome — minimal branded shell for portal action-item pages and
 * the external SigningPage.
 *
 * Action pages (/portal/:token/action/:actionId) historically rendered the
 * action card on bare white. F19 (sign), F33 (acknowledge) looked unbranded
 * vs the portal landing which renders inside ExternalViewLayout (Banks header
 * + Powered-by footer).
 *
 * Theme-3 iter-9: SigningPage (/sign/:signRef) also needs this chrome — it's
 * the customer's first impression of the signing flow and was rendering as a
 * generic "Document signing" box. SigningPage doesn't have a portal token; it
 * authenticates by signing_token via /api/sign/by-link/.../meta. Made the
 * meta URL pluggable via `metaUrl` prop; legacy `token + apiBase` still works
 * for callers on /api/ext/{token}/meta.
 *
 * This shell:
 *   1. Fetches public meta (default: `${apiBase}/api/ext/${token}/meta`, or a
 *      caller-supplied `metaUrl`) to get branding (logo + company name) and
 *      theme. Injects the theme CSS variables.
 *   2. Renders a sticky header with the tenant logo or company name and an
 *      optional project name on the right.
 *   3. Renders children in the same max-w-6xl main container ExternalViewPage
 *      uses, so layout matches the landing page.
 *   4. Renders PoweredByFooter at the bottom.
 *
 * Falls back gracefully if meta fetch fails — children still render with the
 * default theme, no chrome blocks the action card.
 */
import { useEffect, useState } from 'react';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';
import { injectCSSVariables, DEFAULT_THEME } from '@/lib/theme';

interface PortalActionChromeProps {
  /** Portal token — used to build the default meta URL (/api/ext/{token}/meta).
   *  Ignored when `metaUrl` is supplied. */
  token?: string;
  /** API base URL — used to build the default meta URL when `metaUrl` is not
   *  supplied. */
  apiBase: string;
  /** Optional explicit meta URL. Pass for routes that don't sit behind an
   *  /api/ext/{token}/meta endpoint (e.g. SigningPage uses
   *  /api/sign/by-link/{review_id_short}/{raw_token}/meta-equivalent). */
  metaUrl?: string;
  /** Optional pre-resolved branding. Bypasses the meta fetch entirely — useful
   *  when the parent component already loaded the data and just wants the
   *  visual chrome. */
  branding?: {
    logo_url?: string | null;
    company_name?: string | null;
  } | null;
  /** Optional project name shown subtly under the header — gives the action
   *  page a project anchor when the action card itself doesn't lead with it. */
  projectName?: string | null;
  children: React.ReactNode;
}

interface PublicMeta {
  view?: {
    branding?: {
      logo_url?: string | null;
      company_name?: string | null;
    } | null;
    name?: string | null;
  } | null;
  /** /api/ext/{token}/meta returns branding nested inside the theme block,
   *  not under view. PortalActionChrome historically read view.branding
   *  which never matched the real shape — the logo silently fell back to
   *  "Project portal" plain text. Keep both paths to be tolerant of future
   *  schema drift. */
  theme?: {
    branding?: {
      logo_url?: string | null;
      company_name?: string | null;
    } | null;
    [k: string]: unknown;
  } | null;
}

export function PortalActionChrome({
  token,
  apiBase,
  metaUrl,
  branding: brandingProp,
  projectName,
  children,
}: PortalActionChromeProps) {
  const [meta, setMeta] = useState<PublicMeta | null>(null);
  const [themeReady, setThemeReady] = useState(false);

  useEffect(() => {
    // Branding-prop path: no fetch needed; parent already has the data.
    if (brandingProp) {
      injectCSSVariables(DEFAULT_THEME);
      setThemeReady(true);
      return;
    }
    const resolvedUrl = metaUrl || (token ? `${apiBase}/api/ext/${token}/meta` : null);
    if (!resolvedUrl) {
      injectCSSVariables(DEFAULT_THEME);
      setThemeReady(true);
      return;
    }
    let cancelled = false;
    async function loadMeta(url: string) {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          if (!cancelled) {
            injectCSSVariables(DEFAULT_THEME);
            setThemeReady(true);
          }
          return;
        }
        const data = (await res.json()) as PublicMeta;
        if (cancelled) return;
        if (data?.theme) {
          injectCSSVariables(data.theme);
        } else {
          injectCSSVariables(DEFAULT_THEME);
        }
        setMeta(data);
        setThemeReady(true);
      } catch {
        if (!cancelled) {
          injectCSSVariables(DEFAULT_THEME);
          setThemeReady(true);
        }
      }
    }
    loadMeta(resolvedUrl);
    return () => {
      cancelled = true;
    };
  }, [token, apiBase, metaUrl, brandingProp]);

  const logoUrl =
    brandingProp?.logo_url ??
    meta?.theme?.branding?.logo_url ??
    meta?.view?.branding?.logo_url ??
    null;
  const companyName =
    brandingProp?.company_name ??
    meta?.theme?.branding?.company_name ??
    meta?.view?.branding?.company_name ??
    null;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--background)' }}
    >
      {/* Sticky branded header (mirrors ExternalViewLayout chrome) */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          backgroundColor: 'var(--background)',
          borderColor: 'var(--border, #e5e7eb)',
        }}
        data-testid="portal-action-chrome-header"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 min-h-16 py-2 sm:py-0 sm:h-16 flex items-center gap-3">
          <div className="flex items-center gap-2.5 shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName || 'Logo'}
                className="h-8 max-w-[60px] sm:max-w-[120px] object-contain"
              />
            ) : companyName ? (
              <span
                className="text-base font-bold tracking-tight truncate"
                style={{ color: 'var(--foreground)' }}
              >
                {companyName}
              </span>
            ) : (
              <span
                className="text-base font-bold tracking-tight truncate text-muted-foreground"
              >
                Project portal
              </span>
            )}
          </div>
          {projectName && (
            <div className="flex-1 flex justify-center px-1 sm:px-4 min-w-0">
              <h1
                className="text-xs sm:text-sm font-semibold text-center leading-tight break-words"
                style={{
                  color: 'var(--foreground)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {projectName}
              </h1>
            </div>
          )}
          {/* right placeholder for symmetry with ExternalViewLayout */}
          <div className="shrink-0 w-16 sm:w-48" aria-hidden="true" />
        </div>
      </header>

      <main
        className="flex-1 mx-auto w-full max-w-6xl px-3 sm:px-6 py-4 sm:py-8"
        data-testid="portal-action-chrome-main"
        style={{ visibility: themeReady ? 'visible' : 'hidden' }}
      >
        {children}
      </main>

      <PoweredByFooter />
    </div>
  );
}
